// =============================================================================
// MessageBubble/utils.ts — Utilitários compartilhados pelas bolhas.
// =============================================================================

/**
 * Formata a data de uma mensagem para exibição no rodapé da bolha.
 * Mostra apenas horário (HH:MM) para mensagens de hoje;
 * exibe "ontem" ou a data curta para mensagens anteriores.
 */
export function formatBubbleTime(iso: string): string {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '';

  const now = new Date();
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  if (isToday) {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();

  if (isYesterday) {
    return `ontem ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  }

  // Data mais antiga: DD/MM HH:MM
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Formata data completa de separação de dia (ex: "Hoje", "Ontem", "12/06/2026").
 */
export function formatDaySeparator(iso: string): string {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '';

  const now = new Date();
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  if (isToday) return 'Hoje';

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();

  if (isYesterday) return 'Ontem';

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Verifica se duas mensagens caem em dias diferentes (para inserir separador de dia).
 */
export function isDifferentDay(isoA: string, isoB: string): boolean {
  const a = new Date(isoA);
  const b = new Date(isoB);
  return (
    a.getDate() !== b.getDate() ||
    a.getMonth() !== b.getMonth() ||
    a.getFullYear() !== b.getFullYear()
  );
}

/**
 * Extrai um nome de arquivo "plausível" de uma URL de mídia — mesma lógica
 * usada pela MediaBubble para a bolha de documento, agora compartilhada com
 * o nome pré-preenchido do diálogo "Anexar aos documentos" (F46-S11): o
 * WhatsApp raramente entrega um nome de arquivo real, então isto é só um
 * ponto de partida editável, nunca a verdade — o agente pode (e deve) trocar
 * antes de confirmar.
 */
export function fileNameFromUrl(url: string | null): string {
  if (!url) return 'arquivo';
  try {
    const pathname = new URL(url).pathname;
    const parts = pathname.split('/');
    return decodeURIComponent(parts[parts.length - 1] ?? 'arquivo');
  } catch {
    return 'arquivo';
  }
}
